/**
 * A file's history.
 *
 * WHY IT LOOKS LIKE THIS
 *   The question this answers is "which revision did the vendor quote against,
 *   and who changed it after?". So each entry leads with the PERSON and the
 *   WHAT, carries the engineer's own words from the commit, and states the
 *   content hash — because for a binary CAD file the hash is the only thing
 *   that distinguishes one revision from another.
 *
 *   Entries imported from GitLab are labelled as such. A replayed commit is
 *   second-hand evidence and should never be mistaken for something that
 *   happened in Lattice.
 */
import { FilePlus2, FilePenLine, FileX2, FileSymlink, GitBranch } from 'lucide-react'
import type { Change, ChangeKind, ProjectId } from '@lattice/core'
import { useWorkspace, personOf, historyOf } from '@/store/workspace'
import { Avatar, Tooltip, Badge } from './ui/primitives'
import { formatBytes, formatRelative, formatExact } from '@/lib/format'
import { cn } from '@/lib/utils'
import { useMemo } from 'react'

const ICONS: Record<ChangeKind, typeof FilePlus2> = {
  added: FilePlus2,
  modified: FilePenLine,
  deleted: FileX2,
  renamed: FileSymlink,
}

const VERBS: Record<ChangeKind, string> = {
  added: 'added',
  modified: 'modified',
  deleted: 'deleted',
  renamed: 'renamed',
}

export function ProvenanceTimeline({ projectId, path, limit }: {
  projectId: ProjectId
  path: string
  limit?: number
}) {
  const changes = useWorkspace(s => s.changes)
  const roster = useWorkspace(s => s.roster)

  const history = useMemo(() => {
    const all = historyOf(changes, projectId, path)
    return limit ? all.slice(0, limit) : all
  }, [changes, projectId, path, limit])

  if (history.length === 0) {
    return <p className="px-3 py-4 text-sm text-[var(--fg-subtle)]">No recorded history for this file.</p>
  }

  return (
    <ol className="relative px-3 py-1">
      {/* One continuous rule behind the markers reads as a single history
          rather than a stack of unrelated cards. */}
      <div className="absolute left-[21px] top-3 bottom-3 w-px bg-[var(--border)]" aria-hidden />
      {history.map((change, i) => (
        <Entry key={change.id} change={change} roster={roster} isLatest={i === 0} />
      ))}
    </ol>
  )
}

function Entry({ change, roster, isLatest }: {
  change: Change
  roster: ReturnType<typeof useWorkspace.getState>['roster']
  isLatest: boolean
}) {
  const person = personOf(roster, change.authorId)
  const Icon = ICONS[change.kind]

  return (
    <li className="relative flex gap-2.5 py-2">
      <span className={cn(
        'relative z-10 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border bg-[var(--bg)]',
        change.kind === 'added' ? 'border-[color-mix(in_oklch,var(--added)_45%,transparent)]'
          : change.kind === 'modified' ? 'border-[color-mix(in_oklch,var(--modified)_45%,transparent)]'
          : change.kind === 'deleted' ? 'border-[color-mix(in_oklch,var(--deleted)_45%,transparent)]'
          : 'border-[color-mix(in_oklch,var(--renamed)_45%,transparent)]',
      )}>
        <Icon className={cn('h-3 w-3', `kind-${change.kind}`)} strokeWidth={1.75} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm">
          <Avatar user={person} size={16} />
          <span className="font-medium">{person.name}</span>
          <span className={cn('kind-' + change.kind)}>{VERBS[change.kind]}</span>
          {change.kind === 'renamed' && change.prevPath && (
            <span className="truncate text-[var(--fg-muted)]">
              from <span className="font-mono text-xs">{change.prevPath.split('/').pop()}</span>
            </span>
          )}
          <Tooltip label={formatExact(change.at)}>
            <span className="text-xs text-[var(--fg-subtle)]">{formatRelative(change.at)}</span>
          </Tooltip>
          {isLatest && <Badge tone="accent">current</Badge>}
        </div>

        {change.message && (
          <p className="mt-1 text-sm leading-snug text-[var(--fg-muted)]">{change.message}</p>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-[var(--fg-subtle)]">
          {change.sha && (
            <Tooltip label={<span className="font-mono">{change.sha}</span>}>
              <span className="font-mono tabular cursor-help">{change.sha.slice(0, 10)}</span>
            </Tooltip>
          )}
          {change.size !== undefined && <span className="tabular">{formatBytes(change.size)}</span>}
          {change.source === 'git-import' && (
            <Tooltip label="Replayed from the team's GitLab history, not created in Lattice">
              <span className="inline-flex items-center gap-1 cursor-help">
                <GitBranch className="h-3 w-3" strokeWidth={1.75} /> imported
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </li>
  )
}
